import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow
} from "@chakra-ui/react";
import { 
  FiCheckCircle, 
  FiXCircle, 
  FiEye, 
  FiClock,
  FiDollarSign,
  FiUsers,
  FiAlertTriangle,
  FiTrendingUp,
  FiPercent,
  FiShield
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { useAccessControl } from "../../../hooks/useAccessControl";
import { 
  useGetPendingHRApprovals, 
  useApproveHRBatch, 
  useRejectHRBatch,
  useGetCompanyWallet
} from "../../../services/payrollService";
import { formatCurrency, getCurrencySymbol } from "../../../utils/currency";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alert";

// Helper function to format currency with currency code
const formatCurrencyWithCode = (amount, currencyCode) => {
  if (amount === null || amount === undefined) return '0';
  
  const currencySymbols = {
    'USD': '$',
    'NGN': '₦',
    'GHS': '₵',
    'ZAR': 'R',
    'KES': 'KSh',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currencyCode] || '$';
  const numericAmount = parseFloat(amount) || 0;
  
  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${symbol}${formattedAmount}`;
};

const HRApprovals = () => {
  const { user } = useUserContext();
  const { canApproveHR, customerRoles } = useAccessControl();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isApproveOpen, onOpen: onApproveOpen, onClose: onApproveClose } = useDisclosure();
  const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
  const [selectedBatch, setSelectedBatch] = useState(null);
  const cancelRef = React.useRef();
  const [approvalNotes, setApprovalNotes] = useState("");
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // Real API data
  const { data: pendingBatchesData, isLoading: batchesLoading, error: batchesError } = useGetPendingHRApprovals();
  const approveBatchMutation = useApproveHRBatch();
  const rejectBatchMutation = useRejectHRBatch();
  
  // Get wallet balance for approval confirmation
  const { data: walletData } = useGetCompanyWallet();

  // Process the API response data
  const pendingBatches = pendingBatchesData?.data || [];
  const totalCount = pendingBatches.length;
  
  // Calculate total company cost for selected batch
  const calculateTotalCompanyCost = (batch) => {
    if (!batch) return 0;
    const netSalaries = batch.payment_summary?.total_net_salaries || batch.total_amount || 0;
    const transferCharges = batch.total_transfer_charges || 0;
    const processingFees = batch.processing_fees_paid || 0;
    const settlement = batch.payment_summary?.total_settlement_amount || 0;
    return netSalaries + transferCharges + processingFees + settlement;
  };
  
  // Check if wallet has sufficient funds
  const hasSufficientFunds = (batch) => {
    if (!batch || !walletData) return null;
    const totalCost = calculateTotalCompanyCost(batch);
    const availableBalance = walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0;
    return availableBalance >= totalCost;
  };

  const handleViewBatch = (batch) => {
    setSelectedBatch(batch);
    setApprovalNotes("");
    onOpen();
  };

  const handleApproveBatch = async () => {
    try {
      if (selectedBatch?.batch_id) {
        await approveBatchMutation.mutateAsync(selectedBatch.batch_id);
        onApproveClose();
        onClose();
      }
    } catch (error) {
      console.error("Failed to approve batch:", error);
    }
  };

  const handleApproveClick = () => {
    onApproveOpen();
  };

  const handleRejectBatch = async () => {
    try {
      if (selectedBatch?.batch_id) {
        await rejectBatchMutation.mutateAsync({
          batchId: selectedBatch.batch_id,
          rejection_reason: "Rejected by HR"
        });
        onRejectClose();
        onClose();
      }
    } catch (error) {
      console.error("Failed to reject batch:", error);
    }
  };

  const handleRejectClick = () => {
    onRejectOpen();
  };


  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: "$",
      NGN: "₦",
      GHS: "₵",
      KES: "KSh",
      ZAR: "R"
    };
    return symbols[currency] || currency;
  };

  const calculateTotalAmount = (employees) => {
    return employees.reduce((total, emp) => total + (emp.net_salary || 0), 0);
  };

  // Show loading state
  if (batchesLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              HR Approvals
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Loading approval data...
            </Text>
          </VStack>
        </Flex>
        <Box textAlign="center" py={8}>
          <Text color="gray.500">Loading pending HR approvals...</Text>
        </Box>
      </VStack>
    );
  }

  // Show error state
  if (batchesError) {
    return (
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md" color="gray.800">
              HR Approvals
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Error loading approval data
            </Text>
          </VStack>
        </Flex>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading approval data!</AlertTitle>
          <AlertDescription>
            {batchesError?.message || 'Failed to load pending HR approvals'}
          </AlertDescription>
        </Alert>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          {/* <Heading size="md" color="gray.800">
            HR Approvals
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Review and approve payroll batches before accountant review
          </Text> */}
        </VStack>
        <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
          {totalCount} Pending
        </Badge>
      </Flex>

      {/* Summary Statistics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Pending Approvals</StatLabel>
          <StatNumber color="orange.500">{totalCount}</StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            +2 this week
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Total Amount</StatLabel>
           <StatNumber color="blue.500">
             {formatCurrencyWithCode(pendingBatches.reduce((total, batch) => total + (batch.total_amount || 0), 0), pendingBatches[0]?.items?.[0]?.currency || pendingBatches[0]?.currency || 'USD')}
           </StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            +15% from last month
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Total Employees</StatLabel>
          <StatNumber color="green.500">
            {pendingBatches.reduce((total, batch) => total + (batch.employee_count || 0), 0)}
          </StatNumber>
          <StatHelpText>
            <StatArrow type="increase" />
            +5 this month
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Avg. Processing Time</StatLabel>
          <StatNumber color="purple.500">2.5 days</StatNumber>
          <StatHelpText>
            <StatArrow type="decrease" />
            -0.5 days improvement
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Pending Batches Table */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <TableContainer>
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Batch Name</Th>
                <Th>Total Amount</Th>
                <Th>Employees</Th>
                <Th>Created By</Th>
                <Th>Created Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pendingBatches.map((batch) => (
                <Tr key={batch.batch_id}>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="600" color="gray.800">
                        {batch.batch_name}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {batch.batch_id}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                     <Text fontWeight="600" color="gray.800">
                       {formatCurrencyWithCode(batch.total_amount, batch.items?.[0]?.currency || batch.currency)}
                     </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <FiUsers color="gray.500" />
                      <Text>{batch.employee_count}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">
                      {typeof batch.created_by === 'object' ? batch.created_by?.name || 'Unknown' : batch.created_by || 'Unknown'}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600">
                      {new Date(batch.created_at).toLocaleDateString()}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="Review Batch">
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="Review batch"
                          onClick={() => handleViewBatch(batch)}
                        />
                      </Tooltip>
                      
                      {/* Show different actions based on batch status */}
                      {batch.status === 'COMPLETED' ? (
                        <Tooltip label="Batch Completed">
                          <Badge colorScheme="green" variant="subtle">
                            Completed
                          </Badge>
                        </Tooltip>
                      ) : batch.status === 'FAILED' ? (
                        <Tooltip label="Payment Failed">
                          <Badge colorScheme="orange" variant="subtle">
                            Failed
                          </Badge>
                        </Tooltip>
                      ) : batch.status === 'REJECTED' ? (
                        <Tooltip label="Batch Rejected">
                          <Badge colorScheme="red" variant="subtle">
                            Rejected
                          </Badge>
                        </Tooltip>
                      ) : (
                        // For PENDING_HR_APPROVAL or other statuses, show view button (approve/reject in modal)
                        <Tooltip label="View & Review Batch">
                          <IconButton
                            icon={<FiEye />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="View batch details"
                            onClick={() => handleViewBatch(batch)}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Empty State */}
          {pendingBatches.length === 0 && (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>No Pending Approvals</AlertTitle>
            <AlertDescription>
              All payroll batches have been reviewed. New batches will appear here when created.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Batch Review Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Review Payroll Batch: {selectedBatch?.batch_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedBatch && (
              <VStack spacing={6} align="stretch">
                {/* Batch Summary */}
                <Box bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Total Amount</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {formatCurrencyWithCode(selectedBatch.total_amount, selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Employee Count</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {selectedBatch.employee_count}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="blue.700" fontWeight="500">Created By</Text>
                      <Text fontSize="lg" fontWeight="700" color="blue.900">
                        {typeof selectedBatch.created_by === 'object' ? selectedBatch.created_by?.name || 'Unknown' : selectedBatch.created_by || 'Unknown'}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                {/* Payment Summary */}
                {(selectedBatch.payment_summary || selectedBatch.batch_items) && (
                  <Box>
                    <Heading size="sm" color="gray.800" mb={4}>
                      Payment Summary
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                        <Text fontSize="sm" color="green.700" fontWeight="500" mb={1}>
                          Total Net Salaries
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="green.900">
                          {formatCurrencyWithCode(
                            selectedBatch.payment_summary?.total_net_salaries || selectedBatch.total_amount || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="green.600" mt={1}>
                          Employees Receive
                        </Text>
                      </Box>
                      <Box p={4} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                        <Text fontSize="sm" color="purple.700" fontWeight="500" mb={1}>
                          Transfer Charges
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="purple.900">
                          {formatCurrencyWithCode(
                            selectedBatch.total_transfer_charges || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="purple.600" mt={1}>
                          Payment Processing Fees
                        </Text>
                      </Box>
                      <Box p={4} bg="teal.50" borderRadius="md" border="1px solid" borderColor="teal.200">
                        <Text fontSize="sm" color="teal.700" fontWeight="500" mb={1}>
                          Processing Fee (0.5%)
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="teal.900">
                          {formatCurrencyWithCode(
                            selectedBatch.processing_fees_paid || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency
                          )}
                        </Text>
                        {(() => {
                          const items = selectedBatch.items || selectedBatch.batch_items || [];
                          const employeeCount = items.length || selectedBatch.payment_summary?.total_employees || 0;
                          const perEmployeeFee = employeeCount > 0 
                            ? (selectedBatch.processing_fees_paid || 0) / employeeCount 
                            : 0;
                          const currency = selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency;
                          return employeeCount > 0 && perEmployeeFee > 0 ? (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              {formatCurrencyWithCode(perEmployeeFee, currency)} × {employeeCount} employees
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              Service Fee
                            </Text>
                          );
                        })()}
                      </Box>
                      <Box p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                        <Text fontSize="sm" color="orange.700" fontWeight="500" mb={1}>
                          Total Settlement
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="orange.900">
                          {formatCurrencyWithCode(
                            selectedBatch.payment_summary?.total_settlement_amount || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          Company Pays (Tax + Deductions)
                        </Text>
                      </Box>
                      <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                        <Text fontSize="sm" color="blue.700" fontWeight="500" mb={1}>
                          Total Company Cost
                        </Text>
                        <Text fontSize="xl" fontWeight="700" color="blue.900">
                          {formatCurrencyWithCode(
                            (selectedBatch.payment_summary?.total_net_salaries || selectedBatch.total_amount || 0) + 
                            (selectedBatch.total_transfer_charges || 0) +
                            (selectedBatch.processing_fees_paid || 0) +
                            (selectedBatch.payment_summary?.total_settlement_amount || 0),
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency
                          )}
                        </Text>
                        <Text fontSize="xs" color="blue.600" mt={1}>
                          All Costs Combined
                        </Text>
                      </Box>
                      <Box p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                        <Text fontSize="sm" color="gray.700" fontWeight="500" mb={1}>
                          Payment Status
                        </Text>
                        <Badge 
                          colorScheme={selectedBatch.payment_summary?.ready_for_payment ? 'green' : 'yellow'} 
                          fontSize="md" 
                          p={2}
                        >
                          {selectedBatch.payment_summary?.ready_for_payment ? 'Ready' : 'Not Ready'}
                        </Badge>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          {selectedBatch.payment_summary?.total_deductions || 0} Deductions
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                )}

                <Divider />

                {/* Employee Details */}
                <Box>
                  <Heading size="sm" color="gray.800" mb={4}>
                    Employee Payment Details
                  </Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Employee</Th>
                          <Th>Basic Salary</Th>
                          <Th>Allowances</Th>
                          <Th>Bonus</Th>
                          <Th>Leave Allowance</Th>
                          <Th>Deductions</Th>
                          <Th>Tax Amount</Th>
                          <Th>Settlement Amount</Th>
                          <Th>Net Salary</Th>
                          <Th>Transfer Charge</Th>
                          <Th>Pay Type</Th>
                          <Th>Payment Status</Th>
                          <Th>Refund Amount</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(selectedBatch.items?.length > 0 || selectedBatch.batch_items?.length > 0) ? (
                          (selectedBatch.items || selectedBatch.batch_items || []).map((item, index) => (
                            <Tr key={index}>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Text fontWeight="600" color="gray.800">
                                    {item.employee_name || 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    ID: {item.employee_id || 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {item.employee_email || 'N/A'}
                                  </Text>
                                </VStack>
                              </Td>
                              <Td>
                                {formatCurrencyWithCode(item.basic_salary || 0, item.currency || selectedBatch.currency)}
                              </Td>
                              <Td>
                                {formatCurrencyWithCode(item.allowances || 0, item.currency || selectedBatch.currency)}
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="blue.600">
                                  {formatCurrencyWithCode(item.bonus || 0, item.currency || selectedBatch.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="green.600">
                                  {formatCurrencyWithCode(item.leave_allowance || 0, item.currency || selectedBatch.currency)}
                                </Text>
                              </Td>
                              <Td>
                                {formatCurrencyWithCode(item.deductions || 0, item.currency || selectedBatch.currency)}
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="red.600">
                                  {formatCurrencyWithCode(item.tax_amount || 0, item.currency || selectedBatch.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="orange.600">
                                  {formatCurrencyWithCode(item.settlement_amount || 0, item.currency || selectedBatch.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="600" color="green.600">
                                  {formatCurrencyWithCode(item.net_salary || 0, item.currency || selectedBatch.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="500" color="purple.600">
                                  {formatCurrencyWithCode(item.flutterwave_fee || 0, item.currency || selectedBatch.currency)}
                                </Text>
                              </Td>
                              <Td>
                                <Badge colorScheme="blue" variant="subtle">
                                  {item.pay_type === 'regular_pay' ? 'Regular Pay' : 
                                   item.pay_type === 'total_pay' ? 'Total Pay' : 
                                   item.pay_type === 'net_salary' ? 'Net Salary' : 
                                   item.pay_type || 'Regular Pay'}
                                </Badge>
                              </Td>
                              <Td>
                                <Badge 
                                  colorScheme={
                                    item.payment_status === 'PAID' || item.payment_status === 'SUCCESS' || item.payment_status === 'COMPLETED' ? 'green' :
                                    item.payment_status === 'FAILED' ? 'red' :
                                    item.payment_status === 'PROCESSING' ? 'yellow' :
                                    'gray'
                                  } 
                                  variant="subtle"
                                >
                                  {item.payment_status || 'PENDING'}
                                </Badge>
                                {item.failure_reason && (
                                  <Text fontSize="xs" color="red.600" mt={1}>
                                    {item.failure_reason}
                                  </Text>
                                )}
                              </Td>
                              <Td>
                                {item.payment_status === 'FAILED' && item.refund_amount ? (
                                  <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="600" color="red.600">
                                      {formatCurrencyWithCode(item.refund_amount, item.currency || selectedBatch.currency)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      Will be refunded
                                    </Text>
                                  </VStack>
                                ) : (
                                  <Text fontSize="sm" color="gray.400">—</Text>
                                )}
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={11}>
                              <VStack spacing={2} py={4}>
                                <Text color="gray.500">No employee details available</Text>
                              </VStack>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider />

                {/* Completion Details - Show for COMPLETED batches */}
                {selectedBatch?.status === 'COMPLETED' && (
                  <Box bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm" color="green.800">
                        Completion Details
                      </Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <Text fontSize="sm" color="green.700" fontWeight="500">Net Salaries Paid</Text>
                          <Text fontSize="lg" fontWeight="700" color="green.900">
                            {formatCurrencyWithCode(selectedBatch.net_salaries_paid || 0, selectedBatch.items?.[0]?.currency || selectedBatch.currency)}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="green.700" fontWeight="500">Processing Fees Paid</Text>
                          <Text fontSize="lg" fontWeight="700" color="green.900">
                            {formatCurrencyWithCode(selectedBatch.processing_fees_paid || 0, selectedBatch.items?.[0]?.currency || selectedBatch.currency)}
                          </Text>
                        </Box>
                      </SimpleGrid>
                      {selectedBatch.payment_reference && (
                        <Box>
                          <Text fontSize="sm" color="green.700" fontWeight="500">Payment Reference</Text>
                          <Text fontSize="md" color="green.900" mt={1}>
                            {selectedBatch.payment_reference}
                          </Text>
                        </Box>
                      )}
                      <Alert status="success" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          This batch was successfully processed and payments were completed.
                        </AlertDescription>
                      </Alert>
                    </VStack>
                  </Box>
                )}

                {/* Failure Details - Show for FAILED batches */}
                {selectedBatch?.status === 'FAILED' && (
                  <Box bg="orange.50" p={4} borderRadius="lg" border="1px solid" borderColor="orange.200">
                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm" color="orange.800">
                        Failure Details
                      </Heading>
                      {selectedBatch.items?.[0]?.failure_reason && (
                        <Box>
                          <Text fontSize="sm" color="orange.700" fontWeight="500">Failure Reason</Text>
                          <Text fontSize="md" color="orange.900" mt={1}>
                            {selectedBatch.items[0].failure_reason}
                          </Text>
                        </Box>
                      )}
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          This batch failed during payment processing. The accountant will handle payment retry.
                        </AlertDescription>
                      </Alert>
                    </VStack>
                  </Box>
                )}

                {/* Rejection Details - Show only for REJECTED batches */}
                {selectedBatch?.status === 'REJECTED' && (
                  <Box bg="red.50" p={4} borderRadius="lg" border="1px solid" borderColor="red.200">
                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm" color="red.800">
                        Rejection Details
                      </Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <Text fontSize="sm" color="red.700" fontWeight="500">Rejected By</Text>
                          <Text fontSize="lg" fontWeight="700" color="red.900">
                            {selectedBatch.rejected_by || 'Unknown'}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="red.700" fontWeight="500">Rejection Level</Text>
                          <Text fontSize="lg" fontWeight="700" color="red.900">
                            {selectedBatch.rejection_level || 'ACCOUNTANT'}
                          </Text>
                        </Box>
                      </SimpleGrid>
                      <Box>
                        <Text fontSize="sm" color="red.700" fontWeight="500">Rejection Reason</Text>
                        <Text fontSize="md" color="red.900" mt={1}>
                          {selectedBatch.rejection_reason || 'No reason provided'}
                        </Text>
                      </Box>
                      {selectedBatch.rejection_note && (
                        <Box>
                          <Text fontSize="sm" color="red.700" fontWeight="500">Additional Notes</Text>
                          <Text fontSize="md" color="red.900" mt={1}>
                            {selectedBatch.rejection_note}
                          </Text>
                        </Box>
                      )}
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          This batch was rejected and sent back to HR for re-approval.
                        </AlertDescription>
                      </Alert>
                    </VStack>
                  </Box>
                )}

                {/* Approval Notes */}
                <FormControl>
                  <FormLabel>
                    {selectedBatch?.status === 'PENDING_HR_APPROVAL' 
                      ? 'Rejection Reason (Required for rejection)' 
                      : 'Approval Notes (Optional)'
                    }
                  </FormLabel>
                  <Textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder={
                      selectedBatch?.status === 'PENDING_HR_APPROVAL'
                        ? 'Please provide a reason for rejection...'
                        : 'Add any notes or comments about this approval...'
                    }
                    rows={3}
                    isReadOnly={selectedBatch?.status === 'COMPLETED' || selectedBatch?.status === 'REJECTED'}
                    bg={selectedBatch?.status === 'COMPLETED' || selectedBatch?.status === 'REJECTED' ? 'gray.50' : 'white'}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedBatch?.status === 'COMPLETED' ? (
              // For COMPLETED batches, show only Close button
              <Button colorScheme="blue" onClick={onClose}>
                Close
              </Button>
            ) : selectedBatch?.status === 'FAILED' ? (
              // For FAILED batches, show only Close button (HR cannot retry payments)
              <Button colorScheme="blue" onClick={onClose}>
                Close
              </Button>
            ) : selectedBatch?.status === 'REJECTED' ? (
              // For REJECTED batches, show only Close button (read-only)
              <Button colorScheme="blue" onClick={onClose}>
                Close
              </Button>
            ) : (
              // For PENDING_HR_APPROVAL or other statuses, show original buttons
              canApproveHR() ? (
              <>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" mr={3} onClick={handleRejectClick}>
                  Reject Batch
                </Button>
                <Button colorScheme="green" onClick={handleApproveClick}>
                  Approve Batch
                </Button>
              </>
              ) : (
                <Button colorScheme="blue" onClick={onClose}>
                  Close
                </Button>
              )
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Approve Confirmation Dialog */}
      <AlertDialog
        isOpen={isApproveOpen}
        leastDestructiveRef={cancelRef}
        onClose={onApproveClose}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="600px">
            <AlertDialogHeader fontSize="xl" fontWeight="bold" pb={2}>
              <HStack spacing={2}>
                <FiShield color="green" size={24} />
                <Text>Approve Payroll Batch</Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={4} align="stretch">
                {/* Info Message */}
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    This action will send the batch to the accountant for final review and payment processing.
                  </AlertDescription>
                </Alert>

                {/* Batch Info */}
                <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <FiUsers color="blue.600" size={16} />
                      <Text fontSize="sm" fontWeight="600" color="blue.900">
                        Batch: {selectedBatch?.batch_name}
                      </Text>
                    </HStack>
                    <HStack spacing={4}>
                      <Text fontSize="sm" color="blue.700">
                        Employees: <Text as="span" fontWeight="600">{selectedBatch?.employee_count || 0}</Text>
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Currency: <Text as="span" fontWeight="600">{selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency || 'NGN'}</Text>
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Cost Breakdown */}
                {selectedBatch && (
                  <>
                    <Heading size="sm" color="gray.700" mb={2}>
                      Cost Summary
                    </Heading>
                    <SimpleGrid columns={2} spacing={3}>
                      <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                        <HStack spacing={2} mb={1}>
                          <FiDollarSign color="green.600" size={14} />
                          <Text fontSize="xs" color="green.700" fontWeight="500">
                            Net Salaries
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="green.900">
                          {formatCurrencyWithCode(
                            selectedBatch.payment_summary?.total_net_salaries || selectedBatch.total_amount || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                          )}
                        </Text>
                      </Box>
                      <Box p={3} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                        <HStack spacing={2} mb={1}>
                          <FiTrendingUp color="purple.600" size={14} />
                          <Text fontSize="xs" color="purple.700" fontWeight="500">
                            Transfer Charges
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="purple.900">
                          {formatCurrencyWithCode(
                            selectedBatch.total_transfer_charges || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                          )}
                        </Text>
                      </Box>
                      <Box p={3} bg="teal.50" borderRadius="md" border="1px solid" borderColor="teal.200">
                        <HStack spacing={2} mb={1}>
                          <FiPercent color="teal.600" size={14} />
                          <Text fontSize="xs" color="teal.700" fontWeight="500">
                            Processing Fee
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="teal.900">
                          {formatCurrencyWithCode(
                            selectedBatch.processing_fees_paid || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                          )}
                        </Text>
                        {(() => {
                          const items = selectedBatch.items || selectedBatch.batch_items || [];
                          const employeeCount = items.length || selectedBatch.payment_summary?.total_employees || 0;
                          const perEmployeeFee = employeeCount > 0 
                            ? (selectedBatch.processing_fees_paid || 0) / employeeCount 
                            : 0;
                          return employeeCount > 0 && perEmployeeFee > 0 ? (
                            <Text fontSize="xs" color="teal.600" mt={1}>
                              {formatCurrencyWithCode(perEmployeeFee, selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN')} × {employeeCount} employees
                            </Text>
                          ) : null;
                        })()}
                      </Box>
                      <Box p={3} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                        <HStack spacing={2} mb={1}>
                          <FiAlertTriangle color="orange.600" size={14} />
                          <Text fontSize="xs" color="orange.700" fontWeight="500">
                            Settlement
                          </Text>
                        </HStack>
                        <Text fontSize="md" fontWeight="700" color="orange.900">
                          {formatCurrencyWithCode(
                            selectedBatch.payment_summary?.total_settlement_amount || 0,
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                          )}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Divider />

                    {/* Total Cost */}
                    <Box p={4} bg="blue.50" borderRadius="md" border="2px solid" borderColor="blue.300">
                      <HStack justify="space-between">
                        <HStack spacing={2}>
                          <FiDollarSign color="blue.600" size={18} />
                          <Text fontSize="md" fontWeight="700" color="blue.900">
                            Total Company Cost:
                          </Text>
                        </HStack>
                        <Text fontSize="xl" fontWeight="700" color="blue.900">
                          {formatCurrencyWithCode(
                            calculateTotalCompanyCost(selectedBatch),
                            selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                          )}
                        </Text>
                      </HStack>
                    </Box>

                    {/* Wallet Balance Check (Optional for HR - just informational) */}
                    {walletData && (
                      <Box>
                        <VStack align="stretch" spacing={2}>
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">
                              Available Balance:
                            </Text>
                            <Text fontSize="sm" fontWeight="700" color="blue.600">
                              {formatCurrencyWithCode(
                                walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0,
                                walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                              )}
                            </Text>
                          </HStack>
                          {walletData.pending_settlement !== undefined && walletData.pending_settlement > 0 && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Pending Settlement:
                              </Text>
                              <Text fontSize="sm" color="orange.600">
                                {formatCurrencyWithCode(
                                  walletData.pending_settlement,
                                  walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                                )}
                                <Text as="span" fontSize="xs" ml={1} color="gray.500">
                                  (available in 1-5 days)
                                </Text>
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                        {hasSufficientFunds(selectedBatch) === false && (
                          <Alert status="warning" borderRadius="md" fontSize="sm" mt={2}>
                            <AlertIcon />
                            <AlertDescription>
                              Note: Available balance may be insufficient. Accountant will verify funds before processing.
                              {walletData.pending_settlement > 0 && ` Pending settlement: ${formatCurrencyWithCode(
                                walletData.pending_settlement,
                                walletData.currency || selectedBatch.items?.[0]?.currency || selectedBatch.batch_items?.[0]?.currency || selectedBatch.currency || 'NGN'
                              )} (not yet available).`}
                            </AlertDescription>
                          </Alert>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onApproveClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="green" 
                onClick={handleApproveBatch} 
                ml={3} 
                isLoading={approveBatchMutation.isPending}
                leftIcon={<FiCheckCircle />}
              >
                Approve
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        isOpen={isRejectOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRejectClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reject Payroll Batch
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to reject this payroll batch?
              <Box mt={3} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="600">Batch: {selectedBatch?.batch_name}</Text>
                <Text fontSize="sm">Total Amount: {formatCurrencyWithCode(selectedBatch?.total_amount || 0, selectedBatch?.items?.[0]?.currency || selectedBatch?.batch_items?.[0]?.currency || selectedBatch?.currency || 'NGN')}</Text>
                <Text fontSize="sm">Employees: {selectedBatch?.employee_count || 0}</Text>
              </Box>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onRejectClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleRejectBatch} ml={3} isLoading={rejectBatchMutation.isPending}>
                Reject
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default HRApprovals;